/**
 * Schema Zod do payload de Renovacao de Contrato Recorrente (#219 + #241).
 *
 * Renovação NÃO herda do contrato-recorrente (replano 2026-06-09) —
 * estrutura própria com lista livre de itens. Pricing = sum(itens) com
 * reajuste linear opcional + desconto escalonado.
 */
import { z } from 'zod';

import { textoLongoSchema } from './common.js';
import { descontoEscalonadoSchema } from './payload-contrato-recorrente.js';

export const contratoOrigemOmieSchema = z.object({
  codigoOmie: z.number().int().positive(),
  numeroOmie: z.string().min(1).max(60),
  numeroPropostaAnterior: z.string().min(1).max(60).optional(),
  dataInicio: z.string().min(1).max(20),
  dataTermino: z.string().min(1).max(20),
  valorMensalAtual: z.number().nonnegative().finite(),
});

export const tipoReajusteSchema = z.enum(['ipca', 'igpm', 'manual', 'sem-reajuste']);

export const reajusteContratoSchema = z
  .object({
    tipo: tipoReajusteSchema,
    percentual: z.number().min(-50).max(100).optional(),
    motivo: textoLongoSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.tipo !== 'sem-reajuste' && (v.percentual === undefined || v.percentual === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['percentual'],
        message: 'Percentual obrigatório quando reajuste != sem-reajuste',
      });
    }
    if (v.tipo === 'manual' && (!v.motivo || v.motivo.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['motivo'],
        message: 'Motivo obrigatório quando reajuste é manual',
      });
    }
  });

export const novaVigenciaSchema = z.object({
  // ISO YYYY-MM-DD
  dataInicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar em formato YYYY-MM-DD'),
  prazoMeses: z.union([z.literal(12), z.literal(24), z.literal(36)]),
});

/**
 * Item livre da renovação. valorTotal denormalizado pra audit.
 * Tolerância de R$ 0.01 pra arredondamento.
 */
export const itemRenovacaoSchema = z
  .object({
    descricao: z.string().trim().min(1).max(500),
    quantidade: z.number().positive().finite(),
    valorUnitario: z.number().nonnegative().finite(),
    valorTotal: z.number().nonnegative().finite(),
    codigoServicoOmie: z.string().trim().min(1).max(60).optional(),
  })
  .refine(
    (it) => {
      const calc = it.quantidade * it.valorUnitario;
      return Math.abs(calc - it.valorTotal) < 0.011;
    },
    {
      message: 'valorTotal deve ser igual a quantidade × valorUnitario',
      path: ['valorTotal'],
    },
  );

/**
 * Payload renovação simplificado — estrutura própria, NÃO estende contrato-recorrente.
 */
export const renovacaoRecorrentePayloadSchema = z.object({
  contratoOrigem: contratoOrigemOmieSchema,
  itensRenovacao: z.array(itemRenovacaoSchema).min(1).max(100),
  reajuste: reajusteContratoSchema,
  novaVigencia: novaVigenciaSchema,
  descontoEscalonado: descontoEscalonadoSchema.optional(),
  diaFaturamento: z.number().int().min(1).max(28).optional(),
});
